import React, { useEffect, useState } from "react"
import * as dateHelper from "../../booking/helpers/date"
import * as endpoints from "../../api/endpoints"
import da from "date-fns/locale/da"
import enGB from "date-fns/locale/en-GB"
import Svg from "../_svgs/svg"
import SearchOverlay from "../flight-search-hero/search-overlay"
import OutsideClickTrigger from "../../booking/components/common/OutsideClickTrigger"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import { DateTime } from "luxon"

export default function FlightSearchBar(props) {
  const dtLocale = dateHelper.luxonLocale(props.locale)
  const zone = "Europe/Copenhagen"
  const cphTimeConvert = DateTime.local().setLocale(dtLocale).setZone(zone) // gets local time and changes timezone to copenhagen to get correct time
  const cphTime = cphTimeConvert.setZone("local", { keepLocalTime: true }) // locks in copenhagen time and switches back to local timezone. This ensures the time will stay CPH time when we later converts the DateTime to a JS Date object.

  const [q, setQ] = useState("") //string that queries flights.
  const [searchDate, setSearchDate] = useState(cphTime.set({ milliseconds: 0, seconds: 0, minutes: 60 }).minus({ hours: 1 }))
  const [startDate, setStartDate] = useState(cphTime.set({ milliseconds: 0, seconds: 0, minutes: 60 }).minus({ hours: 1 }))
  const [minDate, setMinDate] = useState(startDate.minus({ days: 1 }))

  const [flights, setFlights] = useState([])
  const [direction, setDirection] = useState("D")
  const [showOverlay, setShowOverlay] = useState(false)
  const [datepickerOpen, setDatepickerOpen] = useState(false)
  const [airports, setAirports] = useState([])
  const [airlines, setAirlines] = useState([])
  const [mobileInputFocus, setMobileInputFocus] = useState(false)
  const [overInput, setOverInput] = useState(false)

  // maxTime and minTime currently does not have use for a setter.
  // reconsider if this should be in state or not.
  const [minTime] = useState(cphTime.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }))
  const [maxTime] = useState(cphTime.set({ hours: 23, minutes: 0, seconds: 0, milliseconds: 0 }))

  const initialState = {
    flights,
    direction,
    q,
    showOverlay,
    searchDate,
    minDate,
    datepickerOpen,
    minTime,
    maxTime,
    airports,
    airlines,
    mobileInputFocus,
    startDate,
    overInput,
    search,
  }

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  useEffect(() => {
    // Function to update the state with the current screen width
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth)
    }

    // Add an event listener to the window's resize event
    window.addEventListener("resize", updateScreenWidth)

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", updateScreenWidth)
    }
  }, [])

  useEffect(() => {
    const handleFlightSearchRedirect = (event) => {
      let flightId = document.getElementById("flight-search-input")
      let flightIsFocused = document.activeElement === flightId
      if (event.key === "Enter" && q && flightIsFocused) {
        window.location.href = `${direction === "D" ? props.departuresPage : props.arrivalsPage}?q=${q}&date=${searchDate.toFormat(
          "dd / MM / yyyy",
        )}&time=${searchDate.toFormat("HH")}`
      }
    }
    document.addEventListener("keydown", handleFlightSearchRedirect)
    return () => {
      document.removeEventListener("keydown", handleFlightSearchRedirect)
    }
  }, [q, searchDate, direction])

  useEffect(() => {
    const handleExitFlightSearchForm = (event) => {
      if (event.key === "Escape") {
        setShowOverlay(false)
        const input = document.querySelector("#flight-search-input")
        if (input) {
          input.blur()
        }
      }
    }
    document.addEventListener("keydown", handleExitFlightSearchForm)
    return () => {
      document.removeEventListener("keydown", handleExitFlightSearchForm)
    }
  }, [])

  const handleDirectionChange = (direction, wasFocused) => {
    if (screenWidth < 768) {
      const input = document.getElementById("flight-search-input")
      if (input && wasFocused) {
        input.focus()
        setTimeout(() => {
          setMobileInputFocus(true)
        }, 20)
      }
    }
    setDirection(direction)
  }

  //Calling search whenever Query, searchDate and Direction changes.
  //Validation for if a search should occur happens in the search() method.
  useEffect(() => {
    search()
  }, [q, searchDate, direction])

  useEffect(() => {
    const handleFlightSearchRedirect = (event) => {
      let flightId = document.getElementById("flight-search-input")
      let flightIsFocused = document.activeElement === flightId

      if (event.key === "Enter" && q && flightIsFocused) {
        window.location.href = `${direction === "D" ? props.departuresPage : props.arrivalsPage}?q=${q}&date=${searchDate.toFormat(
          "dd / MM / yyyy",
        )}&time=${searchDate.toFormat("HH")}`
      }
    }

    document.addEventListener("keydown", handleFlightSearchRedirect)

    return () => {
      document.removeEventListener("keydown", handleFlightSearchRedirect)
    }
  }, [q, searchDate, direction])

  useEffect(() => {
    const handleExitFlightSearchForm = (event) => {
      if (event.key === "Escape") {
        setShowOverlay(false)
        const input = document.querySelector("#flight-search-input")
        if (input) {
          input.blur()
        }
      }
    }

    document.addEventListener("keydown", handleExitFlightSearchForm)

    return () => {
      document.removeEventListener("keydown", handleExitFlightSearchForm)
    }
  }, [])

  const search = async () => {
    if (q.length >= (props.minCharsBeforeSearch ?? 2)) {
      const dt = searchDate
      const zone = "Europe/Copenhagen"
      const cphTime = dt.setZone(zone, { keepLocalTime: true }) // set zone to copenhagen and keep local time, which is already cph time
      const start = cphTime.toUTC() // convert to UTC which is what Solr expects
      const end = cphTime.endOf("day").toUTC() // convert to UTC which is what Solr expects
      const flightsData = { direction: direction, query: q, startDateTime: start, endDateTime: end, language: props.locale }

      // use this data and call GetAirports endpoint to get airports from solr core instead of flightinfo
      // const airportsData = { language: props.locale, query: q, limit: props.allFlightsLimit }
      const returns = await Promise.all([getFlights(flightsData), getAirports(flightsData)])
        .then((responses) => {
          return Promise.all(
            responses.map((response) => {
              return response
            }),
          )
        })
        .then((data) => {
          return data
        })
        .catch((err) => {
          console.log(err)
        })
      const flights = returns[0]
      const airports = returns[1]
      const airlines = getAirlines(flights)
      setFlights(flights)
      setAirports(airports)
      setAirlines(airlines)
    }
  }

  const handleDateChange = async (dateInput) => {
    let dateTime = DateTime.fromJSDate(dateInput)
    const startOfMinDate = minDate.startOf("day")
    const startOfNewDate = dateTime.startOf("day")
    const time = dateTime.toFormat("HH:mm")

    setSearchDate((prevState) => {
      const startOfPrevDate = prevState.startOf("day")
      const prevTime = prevState.toFormat("HH:mm")

      if (startOfMinDate.toMillis() === startOfNewDate.toMillis()) {
        if (dateTime <= minDate || time === prevTime) {
          setMinDate(dateTime)
          return dateTime
        }
        return dateTime
      } else {
        if (startOfPrevDate.toMillis() !== startOfNewDate.toMillis()) {
          dateTime = dateTime.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
        }
        return dateTime
      }
    })
  }

  const getAirlines = (flights) => {
    let airlines = []
    if (flights && flights.length > 0) {
      const result = Array.from(new Set(flights.map((f) => f.IataAirline))).map((iata) => {
        return {
          iata: iata,
          name: flights.find((f) => f.IataAirline === iata).Airline,
          nameMobile: flights.find((f) => f.IataAirline === iata).AirlineMobile,
        }
      })
      airlines = result
    }
    return airlines
  }

  const getAirports = async (data) => {
    // return endpoints.getAirports(data)
    try {
      const res = await endpoints.getAirportsFromFlights(data)
      const data_2 = res.data
      let airports = []
      if (data_2) {
        airports = data_2
      }
      return airports
    } catch (err) {
      console.log(err)
    }
  }

  const getFlights = async (data) => {
    try {
      const res = await endpoints.getFlightInfoTable(data)
      const data_2 = res.data
      let flights = []
      if (data_2) {
        flights = data_2
      }
      return flights
    } catch (err) {
      console.log(err)
    }
  }

  const handleInputFocus = () => {
    if (q.length > 0) {
      setShowOverlay(true)
    }
  }

  const handleInput = (event, inputSearch = true) => {
    setQ(event.target.value)
    if (event.target.value.length >= 1) {
      setShowOverlay(true)
    } else {
      setShowOverlay(false)
    }
  }

  const handleCloseOverlay = () => {
    setShowOverlay(false)
    setStartDate(startDate)
  }

  const handleDatepicker = () => {
    setDatepickerOpen(!datepickerOpen)
  }

  const closeDatepicker = () => {
    setDatepickerOpen(false)
  }

  const handleOutsideClick = () => {
    if (!overInput) {
      setShowOverlay(false)
      setDatepickerOpen(false)
    }
  }

  const icon = SelectionFactoryHelper.getFromSelectionFactory(props.icon)

  return (
    <section className={`deck--front`}>
      <header className="section-header section-header-search">
        <div className="section-header__inner">
          <div className="w--normal">
            <div className="flight-searchbox">
              <div className="form">
                <div className="form__section form__section--light form__section--fat">
                  <div className={`form__section__inner ${screenWidth < 768 && showOverlay ? "is-active" : ""}`}>
                    <div className="form__field form__field--text form__field--text-search form__field--text--no-label is-bound is-filled">
                      <span className="form__valid"></span>
                      <div className="headline-container">
                        {props.iconUpload ? <img src={props.iconUpload} alt={"icon"} /> : icon ? <Svg iconName={icon} /> : ""}
                        <h2>{props.mainText}</h2>
                      </div>
                      <input
                        className="form__item"
                        id="flight-search-input"
                        autoComplete="off"
                        autoCapitalize="none"
                        name="q"
                        type="text"
                        placeholder={screenWidth < 768 ? props.searchBoxPlaceholderMobile : props.searchBoxPlaceholder}
                        value={q}
                        onChange={(e) => handleInput(e, true)}
                        onMouseEnter={() => setOverInput(true)}
                        onMouseLeave={() => setOverInput(false)}
                        onFocus={() => handleInputFocus()}
                      />

                      {screenWidth >= 768 ? (
                        <button
                          tabIndex={-1}
                          className={`search-button ${showOverlay ? "active" : ""}`}
                          onClick={showOverlay ? handleCloseOverlay : undefined}
                        >
                          <div className={`search-icon`}></div>
                        </button>
                      ) : (
                        ""
                      )}
                      {screenWidth < 768 && showOverlay ? (
                        <button
                          tabIndex={-1}
                          className={`search-button ${showOverlay ? "active" : ""}`}
                          onClick={showOverlay ? handleCloseOverlay : undefined}
                        >
                          {/* <Svg iconName={"close"} fillColor={"#071143"} /> awaiting feedback from CPH on what to do with this. */}
                        </button>
                      ) : (
                        ""
                      )}
                    </div>
                    <OutsideClickTrigger onOutsideClick={() => (screenWidth >= 768 ? undefined : handleOutsideClick())}>
                      <SearchOverlay
                        {...initialState}
                        {...props}
                        handleDirectionChange={handleDirectionChange}
                        handleDateChange={handleDateChange}
                        locale={props.locale === "da" ? da : enGB}
                        handleDatepicker={handleDatepicker}
                        closeDatepicker={closeDatepicker}
                      />
                    </OutsideClickTrigger>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="clear"></div>
        </div>
      </header>
    </section>
  )
}
